import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Search,
  Bell,
  User,
  LogOut,
  Package,
  ClipboardCheck,
  GitBranch,
  Loader2,
  Shield,
  Users,
  Briefcase,
  Receipt,
  CheckCircle,
  Building2,
  ChevronDown,
  Check,
  Settings,
  Home,
  X,
  Sun,
  Moon,
} from 'lucide-react';
import { useTheme } from 'next-themes';
import { Button } from '@/app/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/app/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/app/components/ui/avatar';
import { Input } from '@/app/components/ui/input';
import { useAuth } from '@/contexts/auth-context';
import { useSearch, useSearchNavigation } from '@/hooks/use-search';
import { useGlobalSearch, useUnreadNotificationCount, useNotifications, useMarkNotificationsRead } from '@/hooks/use-platform';
import { useModules, useOrgTimeZone } from '@/hooks/use-settings';
import { useMemberships, useSwitchOrg } from '@/hooks/use-memberships';
import { usePermissions } from '@/hooks/use-permissions';
import { useShowInvoicing } from '@/hooks/use-show-invoicing';
import { useBranding } from '@/contexts/branding-context';
import { OrganizationLogo } from '@/app/components/navigation/organization-logo';
import { ModuleTabs } from '@/app/components/navigation/module-tabs';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';

interface SearchResultItemProps {
  type: string;
  index: number;
  isSelected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function SearchResultItem({ isSelected, onClick, children }: SearchResultItemProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition-colors ${
        isSelected ? 'bg-blue-50 dark:bg-blue-950 text-blue-900 dark:text-blue-100' : 'hover:bg-gray-50 dark:hover:bg-accent'
      }`}
    >
      {children}
    </button>
  );
}

export function TopNav() {
  const orgTimeZone = useOrgTimeZone();
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const permissions = usePermissions();
  const { branding } = useBranding();
  const { resolvedTheme, setTheme } = useTheme();

  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const mobileSearchRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    query,
    setQuery,
    results,
    isLoading,
    hasResults,
    clearSearch,
  } = useSearch();

  const {
    moveUp,
    moveDown,
    getSelectedItem,
    reset: resetNavigation,
    isSelected,
  } = useSearchNavigation(results);

  const { data: globalResults, isLoading: globalLoading } = useGlobalSearch(query);
  const { data: unreadCount } = useUnreadNotificationCount();
  const { data: notificationsData } = useNotifications();
  const markReadMutation = useMarkNotificationsRead();

  const { data: memberships } = useMemberships();
  const switchOrgMutation = useSwitchOrg();
  const currentOrg = memberships?.find((m) => m.is_current);

  const { data: modulesData } = useModules();
  const enabledModules = modulesData?.modules;
  const showInvoicing = useShowInvoicing();

  const hasGlobalResults = globalResults && (
    (globalResults.results.assets?.length ?? 0) > 0 ||
    (globalResults.results.techs?.length ?? 0) > 0 ||
    (globalResults.results.projects?.length ?? 0) > 0 ||
    (globalResults.results.jsas?.length ?? 0) > 0 ||
    (showInvoicing && (globalResults.results.invoices?.length ?? 0) > 0)
  );

  const handleLogout = async () => {
    if (await logout()) navigate('/login', { replace: true });
  };

  const handleSwitchOrg = (orgId: number) => {
    switchOrgMutation.mutate(orgId, {
      onSuccess: async () => {
        // Refresh the auth context with the new tenant before navigating so
        // the destination page does not mount queries that get wiped by the
        // org-change detection in refreshUser.
        await refreshUser();
        navigate('/', { replace: true });
      },
    });
  };

  const displayName = user?.username || 'User';
  const displayRole = user?.role || 'Member';
  const initials = displayName.charAt(0).toUpperCase();

  const navigateToResult = useCallback((type: string, id: number) => {
    setIsSearchOpen(false);
    clearSearch();
    resetNavigation();

    switch (type) {
      case 'assets':
        navigate(`/assets/${id}`);
        break;
      case 'inspections':
        navigate(`/inspections/${id}`);
        break;
      case 'deployments':
        navigate(`/deployments`);
        break;
      case 'techs':
        navigate(`/crew/techs/${id}`);
        break;
      case 'projects':
        navigate(`/projects/${id}`);
        break;
      case 'jsas':
        navigate(`/safety/jsas/${id}`);
        break;
      case 'invoices':
        navigate(`/projects/invoices/${id}`);
        break;
    }
  }, [navigate, clearSearch, resetNavigation]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isSearchOpen || !hasResults) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        moveDown();
        break;
      case 'ArrowUp':
        e.preventDefault();
        moveUp();
        break;
      case 'Enter': {
        e.preventDefault();
        const selected = getSelectedItem();
        if (selected) {
          navigateToResult(selected.type, selected.item.id);
        }
        break;
      }
      case 'Escape':
        e.preventDefault();
        setIsSearchOpen(false);
        inputRef.current?.blur();
        break;
    }
  }, [isSearchOpen, hasResults, moveDown, moveUp, getSelectedItem, navigateToResult]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (mobileSearchRef.current) {
        if (!mobileSearchRef.current.contains(event.target as Node)) {
          setIsMobileSearchOpen(false);
          setIsSearchOpen(false);
        }
      } else if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setIsSearchOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setIsNotificationsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!query.trim()) return;
    const id = requestAnimationFrame(() => setIsSearchOpen(true));
    return () => cancelAnimationFrame(id);
  }, [query]);

  const handleMarkAllRead = () => {
    markReadMutation.mutate({ all: true });
  };

  const handleNotificationClick = (link: string, id: number) => {
    markReadMutation.mutate({ ids: [id] });
    setIsNotificationsOpen(false);
    if (link) navigate(link);
  };

  const notifications = notificationsData?.notifications ?? [];

  const showGlobalSection = enabledModules && (
    enabledModules.crew ||
    enabledModules.projects ||
    enabledModules.safety ||
    enabledModules.payroll
  );

  return (
    <header className="sticky top-0 z-40 bg-white dark:bg-card border-b border-gray-200 dark:border-border shadow-sm dark:shadow-none print:hidden">
      {/* Top row: logo, search, notifications, user */}
      <div className="flex h-14 items-center gap-x-2 sm:gap-x-4 px-3 sm:px-4 lg:px-6">
        {/* Logo */}
        <Link to="/h3ightboard" className="flex items-center gap-2 flex-shrink-0">
          <OrganizationLogo
            logoUrl={branding?.logo_url}
            organizationName={branding?.name}
            variant="header"
          />
        </Link>

        {/* Search - desktop: inline bar, mobile: icon toggle */}
        {/* Mobile search icon */}
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden h-9 w-9 flex-shrink-0"
          onClick={() => setIsMobileSearchOpen(!isMobileSearchOpen)}
        >
          <Search className="h-5 w-5" />
          <span className="sr-only">Search</span>
        </Button>

        {/* Desktop search bar */}
        <div ref={searchContainerRef} className="relative hidden lg:flex flex-1 max-w-md ml-4">
          <label htmlFor="search-top-nav" className="sr-only">
            Search
          </label>
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            ref={inputRef}
            id="search-top-nav"
            type="search"
            placeholder="Search..."
            className="pl-10 w-full h-9"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => query.trim() && setIsSearchOpen(true)}
            onKeyDown={handleKeyDown}
            autoComplete="off"
          />

          {isSearchOpen && query.trim() && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-card border border-gray-200 dark:border-border rounded-lg shadow-lg overflow-hidden z-50">
              {(isLoading || globalLoading) ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                </div>
              ) : (hasResults || hasGlobalResults) ? (
                <div className="max-h-96 overflow-y-auto">
                  {results && results.assets.length > 0 && (
                    <div>
                      <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-muted-foreground bg-gray-50 dark:bg-secondary border-b dark:border-border">
                        Assets ({results.assets.length})
                      </div>
                      {results.assets.map((asset, index) => (
                        <SearchResultItem
                          key={asset.id}
                          type="assets"
                          index={index}
                          isSelected={isSelected('assets', index)}
                          onClick={() => navigateToResult('assets', asset.id)}
                        >
                          <Package className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{asset.name}</p>
                            <p className="text-xs text-gray-500 truncate">{asset.serial_number}</p>
                          </div>
                        </SearchResultItem>
                      ))}
                    </div>
                  )}

                  {results && results.inspections.length > 0 && (
                    <div>
                      <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-muted-foreground bg-gray-50 dark:bg-secondary border-b border-t dark:border-border">
                        Inspections ({results.inspections.length})
                      </div>
                      {results.inspections.map((inspection, index) => (
                        <SearchResultItem
                          key={inspection.id}
                          type="inspections"
                          index={index}
                          isSelected={isSelected('inspections', index)}
                          onClick={() => navigateToResult('inspections', inspection.id)}
                        >
                          <ClipboardCheck className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{inspection.asset_name}</p>
                            <p className="text-xs text-gray-500">{inspection.date}</p>
                          </div>
                        </SearchResultItem>
                      ))}
                    </div>
                  )}

                  {results && results.deployments.length > 0 && (
                    <div>
                      <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-muted-foreground bg-gray-50 dark:bg-secondary border-b border-t dark:border-border">
                        Deployments ({results.deployments.length})
                      </div>
                      {results.deployments.map((deployment, index) => (
                        <SearchResultItem
                          key={deployment.id}
                          type="deployments"
                          index={index}
                          isSelected={isSelected('deployments', index)}
                          onClick={() => navigateToResult('deployments', deployment.id)}
                        >
                          <GitBranch className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{deployment.asset_name}</p>
                            <p className="text-xs text-gray-500 capitalize">{deployment.action.replace('_', ' ')}</p>
                          </div>
                        </SearchResultItem>
                      ))}
                    </div>
                  )}
                  {showGlobalSection && globalResults && (
                    <>
                      {(globalResults.results.techs?.length ?? 0) > 0 && (
                        <div>
                          <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-muted-foreground bg-blue-50 dark:bg-blue-950 border-b border-t dark:border-border">
                            <Users className="inline h-3 w-3 mr-1" />
                            Technicians ({globalResults.results.techs!.length})
                          </div>
                          {globalResults.results.techs!.map((tech) => (
                            <SearchResultItem
                              key={`tech-${tech.id}`}
                              type="techs"
                              index={0}
                              isSelected={false}
                              onClick={() => navigateToResult('techs', tech.id)}
                            >
                              <Users className="h-4 w-4 text-blue-400 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{tech.display}</p>
                                <p className="text-xs text-gray-500 truncate">{tech.job_title}</p>
                              </div>
                            </SearchResultItem>
                          ))}
                        </div>
                      )}

                      {(globalResults.results.projects?.length ?? 0) > 0 && (
                        <div>
                          <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-muted-foreground bg-blue-50 dark:bg-blue-950 border-b border-t dark:border-border">
                            <Briefcase className="inline h-3 w-3 mr-1" />
                            Projects ({globalResults.results.projects!.length})
                          </div>
                          {globalResults.results.projects!.map((project) => (
                            <SearchResultItem
                              key={`project-${project.id}`}
                              type="projects"
                              index={0}
                              isSelected={false}
                              onClick={() => navigateToResult('projects', project.id)}
                            >
                              <Briefcase className="h-4 w-4 text-blue-400 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{project.display}</p>
                                <p className="text-xs text-gray-500 truncate">{project.project_number}</p>
                              </div>
                            </SearchResultItem>
                          ))}
                        </div>
                      )}

                      {(globalResults.results.jsas?.length ?? 0) > 0 && (
                        <div>
                          <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-muted-foreground bg-blue-50 dark:bg-blue-950 border-b border-t dark:border-border">
                            <Shield className="inline h-3 w-3 mr-1" />
                            JSAs ({globalResults.results.jsas!.length})
                          </div>
                          {globalResults.results.jsas!.map((jsa) => (
                            <SearchResultItem
                              key={`jsa-${jsa.id}`}
                              type="jsas"
                              index={0}
                              isSelected={false}
                              onClick={() => navigateToResult('jsas', jsa.id)}
                            >
                              <Shield className="h-4 w-4 text-green-400 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{jsa.display}</p>
                                <p className="text-xs text-gray-500 capitalize">{jsa.status}</p>
                              </div>
                            </SearchResultItem>
                          ))}
                        </div>
                      )}

                      {showInvoicing && (globalResults.results.invoices?.length ?? 0) > 0 && (
                        <div>
                          <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-muted-foreground bg-blue-50 dark:bg-blue-950 border-b border-t dark:border-border">
                            <Receipt className="inline h-3 w-3 mr-1" />
                            Invoices ({globalResults.results.invoices!.length})
                          </div>
                          {globalResults.results.invoices!.map((invoice) => (
                            <SearchResultItem
                              key={`invoice-${invoice.id}`}
                              type="invoices"
                              index={0}
                              isSelected={false}
                              onClick={() => navigateToResult('invoices', invoice.id)}
                            >
                              <Receipt className="h-4 w-4 text-orange-400 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{invoice.display}</p>
                                <p className="text-xs text-gray-500">{invoice.total}</p>
                              </div>
                            </SearchResultItem>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div className="py-8 text-center text-sm text-gray-500 dark:text-muted-foreground">
                  No results found for &quot;{query}&quot;
                </div>
              )}
            </div>
          )}
        </div>

        {/* Spacer to push right side items */}
        <div className="flex-1 lg:hidden" />

        {/* Right side: notifications, org switcher, user menu */}
        <div className="flex items-center gap-x-1 sm:gap-x-2 lg:gap-x-3 ml-auto">
          {/* Theme Toggle */}
          <Button
            variant="ghost"
            size="icon"
            className="relative h-9 w-9"
            onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
          >
            <Sun className="h-5 w-5 rotate-0 scale-100 transition-transform dark:rotate-90 dark:scale-0" />
            <Moon className="absolute h-5 w-5 rotate-90 scale-0 transition-transform dark:rotate-0 dark:scale-100" />
            <span className="sr-only">Toggle theme</span>
          </Button>

          {/* Notification Bell */}
          <div ref={notificationsRef} className="relative">
            <Button
              variant="ghost"
              size="icon"
              className="relative h-9 w-9"
              onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
            >
              <Bell className="h-5 w-5" />
              {(unreadCount ?? 0) > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                  {unreadCount! > 9 ? '9+' : unreadCount}
                </span>
              )}
              <span className="sr-only">View notifications</span>
            </Button>

            {isNotificationsOpen && (
              <div className="absolute right-0 top-full mt-2 w-[calc(100vw-2rem)] sm:w-96 max-w-96 bg-white dark:bg-card border border-gray-200 dark:border-border rounded-lg shadow-lg overflow-hidden z-50">
                <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50 dark:bg-secondary">
                  <h3 className="font-semibold text-sm">Notifications</h3>
                  {notifications.length > 0 && (
                    <button
                      type="button"
                      className="text-xs text-blue-600 hover:underline"
                      onClick={handleMarkAllRead}
                    >
                      Mark all read
                    </button>
                  )}
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notifications.length > 0 ? (
                    notifications.slice(0, 20).map((n) => (
                      <button
                        key={n.id}
                        type="button"
                        onClick={() => handleNotificationClick(n.link, n.id)}
                        className={`w-full text-left px-4 py-3 border-b last:border-b-0 transition-colors ${
                          n.is_read ? 'bg-white dark:bg-card' : 'bg-blue-50 dark:bg-blue-950'
                        } hover:bg-gray-50 dark:hover:bg-accent`}
                      >
                        <div className="flex items-start gap-2">
                          <div className="mt-0.5">
                            {n.is_read ? (
                              <CheckCircle className="h-4 w-4 text-gray-300" />
                            ) : (
                              <Bell className="h-4 w-4 text-blue-500" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm ${n.is_read ? 'text-gray-600 dark:text-muted-foreground' : 'font-medium text-gray-900 dark:text-foreground'}`}>
                              {n.title}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5 truncate">{n.message}</p>
                            <p className="text-xs text-gray-400 mt-1">
                              {formatDateTimeInTimeZone(n.created_at, orgTimeZone)}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="py-8 text-center text-sm text-gray-500 dark:text-muted-foreground">
                      No notifications
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Org Switcher */}
          {memberships && memberships.length > 1 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-1.5 max-w-[140px] sm:max-w-[200px] h-9 px-2 sm:px-3">
                  <Building2 className="h-4 w-4 text-gray-500 dark:text-muted-foreground flex-shrink-0" />
                  <span className="hidden md:inline truncate min-w-0 text-sm font-medium">
                    {currentOrg?.organization_name ?? 'Select Org'}
                  </span>
                  <ChevronDown className="hidden sm:inline h-3 w-3 text-gray-400 flex-shrink-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel>Switch Organization</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {memberships.map((m) => (
                  <DropdownMenuItem
                    key={m.id}
                    onClick={() => !m.is_current && handleSwitchOrg(m.organization_id)}
                    className="flex items-center justify-between"
                    disabled={switchOrgMutation.isPending}
                  >
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="truncate font-medium">{m.organization_name}</span>
                      <span className="text-xs text-gray-500 capitalize">{m.role}</span>
                    </div>
                    {m.is_current && <Check className="h-4 w-4 text-blue-600 flex-shrink-0 ml-2" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative flex items-center gap-2 h-9">
                <Avatar className="h-7 w-7">
                  <AvatarFallback className="bg-blue-600 text-white text-xs">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden lg:inline text-sm font-medium">{displayName}</span>
                <ChevronDown className="hidden lg:inline h-3 w-3 text-gray-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span>{displayName}</span>
                  <span className="text-xs font-normal text-gray-500 capitalize">{displayRole}</span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/my-dashboard')}>
                <Home className="mr-2 h-4 w-4" />
                My Dashboard
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/profile')}>
                <User className="mr-2 h-4 w-4" />
                My Profile & Settings
              </DropdownMenuItem>
              {permissions.canManageOrganization && (
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  <Settings className="mr-2 h-4 w-4" />
                  Organization Settings
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Mobile search expansion */}
      {isMobileSearchOpen && (
        <div ref={mobileSearchRef} className="lg:hidden border-t border-gray-100 dark:border-border px-3 py-2 bg-white dark:bg-card">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              type="search"
              placeholder="Search..."
              className="pl-10 pr-10 w-full h-9"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => query.trim() && setIsSearchOpen(true)}
              onKeyDown={handleKeyDown}
              autoComplete="off"
              autoFocus
            />
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
              onClick={() => {
                setIsMobileSearchOpen(false);
                setIsSearchOpen(false);
                clearSearch();
              }}
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Mobile search results */}
          {isSearchOpen && query.trim() && (
            <div className="mt-1 bg-white dark:bg-card border border-gray-200 dark:border-border rounded-lg shadow-lg overflow-hidden max-h-[60vh] overflow-y-auto">
              {(isLoading || globalLoading) ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                </div>
              ) : (hasResults || hasGlobalResults) ? (
                <div>
                  {results && results.assets.length > 0 && (
                    <div>
                      <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-muted-foreground bg-gray-50 dark:bg-secondary border-b dark:border-border">
                        Assets ({results.assets.length})
                      </div>
                      {results.assets.map((asset, index) => (
                        <SearchResultItem
                          key={asset.id}
                          type="assets"
                          index={index}
                          isSelected={isSelected('assets', index)}
                          onClick={() => { navigateToResult('assets', asset.id); setIsMobileSearchOpen(false); }}
                        >
                          <Package className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{asset.name}</p>
                            <p className="text-xs text-gray-500 truncate">{asset.serial_number}</p>
                          </div>
                        </SearchResultItem>
                      ))}
                    </div>
                  )}
                  {results && results.inspections.length > 0 && (
                    <div>
                      <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-muted-foreground bg-gray-50 dark:bg-secondary border-b border-t dark:border-border">
                        Inspections ({results.inspections.length})
                      </div>
                      {results.inspections.map((inspection, index) => (
                        <SearchResultItem
                          key={inspection.id}
                          type="inspections"
                          index={index}
                          isSelected={isSelected('inspections', index)}
                          onClick={() => { navigateToResult('inspections', inspection.id); setIsMobileSearchOpen(false); }}
                        >
                          <ClipboardCheck className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{inspection.asset_name}</p>
                            <p className="text-xs text-gray-500">{inspection.date}</p>
                          </div>
                        </SearchResultItem>
                      ))}
                    </div>
                  )}
                  {results && results.deployments.length > 0 && (
                    <div>
                      <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-muted-foreground bg-gray-50 dark:bg-secondary border-b border-t dark:border-border">
                        Deployments ({results.deployments.length})
                      </div>
                      {results.deployments.map((deployment, index) => (
                        <SearchResultItem
                          key={deployment.id}
                          type="deployments"
                          index={index}
                          isSelected={isSelected('deployments', index)}
                          onClick={() => { navigateToResult('deployments', deployment.id); setIsMobileSearchOpen(false); }}
                        >
                          <GitBranch className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{deployment.asset_name}</p>
                            <p className="text-xs text-gray-500 capitalize">{deployment.action.replace('_', ' ')}</p>
                          </div>
                        </SearchResultItem>
                      ))}
                    </div>
                  )}
                  {showGlobalSection && globalResults && (
                    <>
                      {(globalResults.results.techs?.length ?? 0) > 0 && (
                        <div>
                          <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-muted-foreground bg-blue-50 dark:bg-blue-950 border-b border-t dark:border-border">
                            <Users className="inline h-3 w-3 mr-1" />
                            Technicians ({globalResults.results.techs!.length})
                          </div>
                          {globalResults.results.techs!.map((tech) => (
                            <SearchResultItem
                              key={`tech-${tech.id}`}
                              type="techs"
                              index={0}
                              isSelected={false}
                              onClick={() => { navigateToResult('techs', tech.id); setIsMobileSearchOpen(false); }}
                            >
                              <Users className="h-4 w-4 text-blue-400 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{tech.display}</p>
                                <p className="text-xs text-gray-500 truncate">{tech.job_title}</p>
                              </div>
                            </SearchResultItem>
                          ))}
                        </div>
                      )}
                      {(globalResults.results.projects?.length ?? 0) > 0 && (
                        <div>
                          <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-muted-foreground bg-blue-50 dark:bg-blue-950 border-b border-t dark:border-border">
                            <Briefcase className="inline h-3 w-3 mr-1" />
                            Projects ({globalResults.results.projects!.length})
                          </div>
                          {globalResults.results.projects!.map((project) => (
                            <SearchResultItem
                              key={`project-${project.id}`}
                              type="projects"
                              index={0}
                              isSelected={false}
                              onClick={() => { navigateToResult('projects', project.id); setIsMobileSearchOpen(false); }}
                            >
                              <Briefcase className="h-4 w-4 text-blue-400 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{project.display}</p>
                                <p className="text-xs text-gray-500 truncate">{project.project_number}</p>
                              </div>
                            </SearchResultItem>
                          ))}
                        </div>
                      )}
                      {(globalResults.results.jsas?.length ?? 0) > 0 && (
                        <div>
                          <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-muted-foreground bg-blue-50 dark:bg-blue-950 border-b border-t dark:border-border">
                            <Shield className="inline h-3 w-3 mr-1" />
                            JSAs ({globalResults.results.jsas!.length})
                          </div>
                          {globalResults.results.jsas!.map((jsa) => (
                            <SearchResultItem
                              key={`jsa-${jsa.id}`}
                              type="jsas"
                              index={0}
                              isSelected={false}
                              onClick={() => { navigateToResult('jsas', jsa.id); setIsMobileSearchOpen(false); }}
                            >
                              <Shield className="h-4 w-4 text-green-400 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{jsa.display}</p>
                                <p className="text-xs text-gray-500 capitalize">{jsa.status}</p>
                              </div>
                            </SearchResultItem>
                          ))}
                        </div>
                      )}
                      {showInvoicing && (globalResults.results.invoices?.length ?? 0) > 0 && (
                        <div>
                          <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-muted-foreground bg-blue-50 dark:bg-blue-950 border-b border-t dark:border-border">
                            <Receipt className="inline h-3 w-3 mr-1" />
                            Invoices ({globalResults.results.invoices!.length})
                          </div>
                          {globalResults.results.invoices!.map((invoice) => (
                            <SearchResultItem
                              key={`invoice-${invoice.id}`}
                              type="invoices"
                              index={0}
                              isSelected={false}
                              onClick={() => { navigateToResult('invoices', invoice.id); setIsMobileSearchOpen(false); }}
                            >
                              <Receipt className="h-4 w-4 text-orange-400 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{invoice.display}</p>
                                <p className="text-xs text-gray-500">{invoice.total}</p>
                              </div>
                            </SearchResultItem>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div className="py-8 text-center text-sm text-gray-500 dark:text-muted-foreground">
                  No results found for &quot;{query}&quot;
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <ModuleTabs />
    </header>
  );
}
